/**
 * ProviderFinance.jsx — UI-B
 * ==========================
 *
 * Provider Finance surface. Consumes the canonical UI-A composition:
 *   GET /api/finance/provider/{clinic_id}/statement
 *   GET /api/finance/provider/{clinic_id}/booking/{booking_id}/detail
 *
 * Strict operational-accounting layout: no donuts, no pie charts, no
 * decorative widgets. Zero manual-payout affordance.
 *
 * Sections:
 *   A. Customer Payment Position   — Customer Funds Received (per PSP+currency)
 *   B. Provider Earnings Position  — Gross Revenue / Fees & Taxes /
 *      Net Earnings / Pending Settlement / Settled / Reversed
 *   C. Customer / booking search   — reuses /api/clinic/accountant/receivables
 *   D. Booking drill-down          — reuses the new UI-B booking detail
 *
 * Provider settlement is automatic (F6C). This surface is READ-ONLY.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Search, X } from 'lucide-react';

import { api } from '../../utils/api';
import { Pill } from '../../components/journey-ux';
import DirectCollectionsPanel from '../../components/finance/DirectCollectionsPanel';

// ---------------------------------------------------------------------------
// Currency helpers — every amount comes from PG as *_minor_units (BIGINT
// integer). No FX. Formatting is per-bucket in the bucket's own currency.
// ---------------------------------------------------------------------------
const CURRENCY_MINOR_DIVISOR = { EUR: 100, USD: 100, GBP: 100, TRY: 100 };

const fmtMinor = (minor, currency) => {
  if (minor == null) return '—';
  const divisor = CURRENCY_MINOR_DIVISOR[currency] || 100;
  const value = Number(minor) / divisor;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || ''}`.trim();
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return '—';
  }
};

const SETTLEMENT_TONE = {
  completed: 'emerald',
  pending: 'amber',
  failed: 'rose',
};

const MILESTONE_TONE = {
  pending: 'slate',
  awaiting_confirmation: 'amber',
  achieved: 'sky',
  released: 'emerald',
  frozen: 'rose',
  refund_pending: 'rose',
  cancelled: 'slate',
};

// ---------------------------------------------------------------------------
// Overview bucket card — one card per (PSP, currency) rail
// ---------------------------------------------------------------------------
const BucketCard = ({ bucket }) => {
  const { t } = useTranslation();
  const psp = bucket.psp_provider || t('provider.finance.unknownPsp');
  const cur = bucket.currency || '—';
  return (
    <section
      className="border border-slate-200 rounded-md bg-white p-4"
      data-testid={`finance-bucket-${bucket.psp_provider || 'na'}-${bucket.currency || 'na'}`}
    >
      <header className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            {t('provider.finance.rail.label')}
          </div>
          <div className="text-[15px] font-semibold text-slate-800">
            {psp} <span className="text-slate-400">·</span> {cur}
          </div>
        </div>
      </header>

      {/* A · Customer Payment Position (NOT provider revenue) */}
      <div className="mt-2 mb-3 rounded border border-sky-100 bg-sky-50/60 p-3">
        <div className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold mb-1">
          {t('provider.finance.customerPosition.title')}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-slate-700">
            {t('provider.finance.customerPosition.received')}
          </span>
          <span
            className="text-[15px] font-semibold text-slate-900"
            data-testid={`bucket-funded-${bucket.psp_provider || 'na'}-${bucket.currency || 'na'}`}
          >
            {fmtMinor(bucket.funded_minor_units, bucket.currency)}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          {t('provider.finance.customerPosition.helper')}
        </p>
      </div>

      {/* B · Provider Earnings Position */}
      <div className="rounded border border-slate-200 bg-white p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          {t('provider.finance.providerEarnings.title')}
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
          <dt className="text-slate-500">{t('provider.finance.providerEarnings.grossRevenue')}</dt>
          <dd className="text-right text-slate-800 font-medium">
            {fmtMinor(bucket.earned_minor_units + bucket.fees_and_taxes_micros_derived, bucket.currency)}
          </dd>

          <dt className="text-slate-500">{t('provider.finance.providerEarnings.feesAndTaxes')}</dt>
          <dd className="text-right text-slate-800 font-medium">
            {fmtMinor(bucket.fees_and_taxes_micros_derived, bucket.currency)}
          </dd>

          <dt className="text-slate-800 font-semibold border-t border-slate-100 pt-1.5 mt-1">
            {t('provider.finance.providerEarnings.netEarnings')}
          </dt>
          <dd className="text-right text-slate-900 font-semibold border-t border-slate-100 pt-1.5 mt-1">
            {fmtMinor(bucket.earned_minor_units, bucket.currency)}
          </dd>

          <dt className="text-amber-700">
            {t('provider.finance.providerEarnings.pendingSettlement')}
          </dt>
          <dd className="text-right text-amber-800 font-medium">
            {fmtMinor(bucket.settlement_pending_minor_units, bucket.currency)}
          </dd>

          <dt className="text-emerald-700">
            {t('provider.finance.providerEarnings.settled')}
          </dt>
          <dd className="text-right text-emerald-800 font-medium">
            {fmtMinor(bucket.settled_minor_units, bucket.currency)}
          </dd>

          <dt className="text-rose-700">
            {t('provider.finance.providerEarnings.reversed')}
          </dt>
          <dd className="text-right text-rose-800 font-medium">
            {fmtMinor(bucket.reversed_minor_units, bucket.currency)}
          </dd>
        </dl>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Booking Detail Drawer
// ---------------------------------------------------------------------------
const BookingDetailDrawer = ({ clinicId, bookingId, onClose }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/finance/provider/${clinicId}/booking/${bookingId}/detail`)
      .then((res) => {
        if (cancelled) return;
        setDetail(res?.data || res);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.message
          || err?.message
          || t('provider.finance.detail.errorGeneric');
        // P0-FIN-ISOLATION-01 — a refused read must REPLACE the previously
        // rendered figures, never sit next to them. A mid-session revocation
        // or context change must not leave stale money on screen.
        setDetail(null);
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clinicId, bookingId, t]);

  const currency = detail?.currency || detail?.contract_currency;

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="booking-detail-drawer">
      <button
        type="button"
        aria-label={t('provider.finance.detail.closeAria')}
        className="flex-1 bg-slate-900/30"
        onClick={onClose}
        data-testid="booking-detail-backdrop"
      />
      <aside className="w-[min(760px,100%)] bg-white border-l border-slate-200 overflow-y-auto">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('provider.finance.detail.title')}
            </div>
            <div className="text-[13px] font-semibold text-slate-800 truncate" data-testid="detail-booking-id">
              {bookingId}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto p-1.5 rounded hover:bg-slate-100 text-slate-500"
            data-testid="booking-detail-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {loading && (
          <div className="p-6 text-[13px] text-slate-500" data-testid="detail-loading">
            {t('provider.finance.detail.loading')}
          </div>
        )}
        {error && !loading && (
          <div className="p-6 text-[13px] text-rose-700" data-testid="detail-error">
            {error}
          </div>
        )}

        {detail && !loading && !error && (
          <div className="p-5 space-y-5">
            {/* Header identity */}
            <section className="rounded border border-slate-200 bg-slate-50/60 p-3">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                <dt className="text-slate-500">{t('provider.finance.detail.patient')}</dt>
                <dd className="text-slate-800 font-medium" data-testid="detail-patient-id">
                  {detail.patient_id || '—'}
                </dd>
                <dt className="text-slate-500">{t('provider.finance.detail.bookingRef')}</dt>
                <dd className="text-slate-800 font-medium">{detail.booking_id}</dd>
                <dt className="text-slate-500">{t('provider.finance.detail.contractDate')}</dt>
                <dd className="text-slate-800 font-medium">{fmtDate(detail.contract_date)}</dd>
                <dt className="text-slate-500">{t('provider.finance.detail.psp')}</dt>
                <dd className="text-slate-800 font-medium">{detail.psp_provider || '—'}</dd>
                <dt className="text-slate-500">{t('provider.finance.detail.currency')}</dt>
                <dd className="text-slate-800 font-medium">{detail.currency || '—'}</dd>
                <dt className="text-slate-500">{t('provider.finance.detail.contracted')}</dt>
                <dd className="text-slate-800 font-medium" data-testid="detail-contracted">
                  {fmtMinor(detail.contracted_amount_minor_units, detail.contract_currency)}
                </dd>
              </dl>
            </section>

            {/* Rollup */}
            <section className="rounded border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {t('provider.finance.detail.rollup')}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                <dt className="text-sky-700">{t('provider.finance.customerPosition.received')}</dt>
                <dd className="text-right text-sky-900 font-medium" data-testid="detail-funds-received">
                  {fmtMinor(detail.rollup.customer_funds_received_minor_units, currency)}
                </dd>
                <dt className="text-slate-500">{t('provider.finance.providerEarnings.grossRevenue')}</dt>
                <dd className="text-right text-slate-800 font-medium">
                  {fmtMinor(detail.rollup.earned_gross_minor_units, currency)}
                </dd>
                <dt className="text-slate-500">{t('provider.finance.providerEarnings.feesAndTaxes')}</dt>
                <dd className="text-right text-slate-800 font-medium">
                  {fmtMinor(detail.rollup.fees_and_taxes_minor_units, currency)}
                </dd>
                <dt className="text-slate-800 font-semibold border-t border-slate-100 pt-1.5 mt-1">
                  {t('provider.finance.providerEarnings.netEarnings')}
                </dt>
                <dd className="text-right text-slate-900 font-semibold border-t border-slate-100 pt-1.5 mt-1"
                    data-testid="detail-net-earnings">
                  {fmtMinor(detail.rollup.net_earnings_minor_units, currency)}
                </dd>
                <dt className="text-amber-700">{t('provider.finance.providerEarnings.pendingSettlement')}</dt>
                <dd className="text-right text-amber-800 font-medium">
                  {fmtMinor(detail.rollup.settlement_pending_minor_units, currency)}
                </dd>
                <dt className="text-emerald-700">{t('provider.finance.providerEarnings.settled')}</dt>
                <dd className="text-right text-emerald-800 font-medium" data-testid="detail-settled">
                  {fmtMinor(detail.rollup.settled_minor_units, currency)}
                </dd>
                <dt className="text-rose-700">{t('provider.finance.providerEarnings.reversed')}</dt>
                <dd className="text-right text-rose-800 font-medium">
                  {fmtMinor(detail.rollup.reversed_minor_units, currency)}
                </dd>
              </dl>
            </section>

            {/* Milestones */}
            <section>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {t('provider.finance.detail.milestones')}
              </div>
              {detail.milestones.length === 0 ? (
                <div className="text-[12px] text-slate-500" data-testid="detail-milestones-empty">
                  {t('provider.finance.detail.milestonesEmpty')}
                </div>
              ) : (
                <div className="overflow-x-auto border border-slate-200 rounded">
                  <table className="w-full text-[12px]" data-testid="detail-milestones-table">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.seq')}</th>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.label')}</th>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.status')}</th>
                        <th className="text-right px-2 py-1.5">{t('provider.finance.detail.col.amount')}</th>
                        <th className="text-right px-2 py-1.5">{t('provider.finance.detail.col.commission')}</th>
                        <th className="text-right px-2 py-1.5">{t('provider.finance.detail.col.vat')}</th>
                        <th className="text-right px-2 py-1.5">{t('provider.finance.detail.col.net')}</th>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.settlement')}</th>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.settledAt')}</th>
                        <th className="text-right px-2 py-1.5">{t('provider.finance.detail.col.reversed')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.milestones.map((m) => (
                        <tr key={m.milestone_id} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 text-slate-800 font-mono">{m.seq}</td>
                          <td className="px-2 py-1.5 text-slate-800">{m.label}</td>
                          <td className="px-2 py-1.5">
                            <Pill tone={MILESTONE_TONE[m.status] || 'slate'}>
                              {t(`provider.finance.milestone.status.${m.status}`, { defaultValue: m.status })}
                            </Pill>
                          </td>
                          <td className="px-2 py-1.5 text-right text-slate-800">{fmtMinor(m.amount_minor_units, currency)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-700">{fmtMinor(m.commission_amount_minor_units, currency)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-700">{fmtMinor(m.vat_amount_minor_units, currency)}</td>
                          <td className="px-2 py-1.5 text-right text-slate-900 font-semibold">{fmtMinor(m.provider_net_minor_units, currency)}</td>
                          <td className="px-2 py-1.5">
                            {m.settlement_status
                              ? <Pill tone={SETTLEMENT_TONE[m.settlement_status] || 'slate'}>
                                  {t(`provider.finance.milestone.settlement.${m.settlement_status}`, { defaultValue: m.settlement_status })}
                                </Pill>
                              : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-slate-700">{fmtDate(m.settled_at)}</td>
                          <td className="px-2 py-1.5 text-right text-rose-700">
                            {m.reversed_amount_minor_units > 0
                              ? fmtMinor(m.reversed_amount_minor_units, currency)
                              : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Funding events */}
            <section>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {t('provider.finance.detail.fundingEvents')}
              </div>
              {detail.funding_events.length === 0 ? (
                <div className="text-[12px] text-slate-500" data-testid="detail-funding-empty">
                  {t('provider.finance.detail.fundingEmpty')}
                </div>
              ) : (
                <div className="overflow-x-auto border border-slate-200 rounded">
                  <table className="w-full text-[12px]">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                      <tr>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.receivedAt')}</th>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.psp')}</th>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.currency')}</th>
                        <th className="text-right px-2 py-1.5">{t('provider.finance.detail.col.amount')}</th>
                        <th className="text-left px-2 py-1.5">{t('provider.finance.detail.col.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.funding_events.map((e) => (
                        <tr key={e.funding_event_id} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 text-slate-800">{fmtDate(e.received_at_utc)}</td>
                          <td className="px-2 py-1.5 text-slate-800">{e.psp_provider || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-800">{e.currency}</td>
                          <td className="px-2 py-1.5 text-right text-slate-800">{fmtMinor(e.amount_minor_units, e.currency)}</td>
                          <td className="px-2 py-1.5">
                            <Pill tone={e.status === 'received' ? 'sky' : 'rose'}>{t(`common.status.${e.status}`, { defaultValue: e.status })}</Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main surface
// ---------------------------------------------------------------------------
const ProviderFinance = ({ clinicId }) => {
  const { t } = useTranslation();
  const [statement, setStatement] = useState(null);
  const [statementError, setStatementError] = useState(null);
  const [statementLoading, setStatementLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [receivables, setReceivables] = useState([]);
  const [receivablesLoading, setReceivablesLoading] = useState(false);

  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const loadStatement = useCallback(() => {
    if (!clinicId) return;
    setStatementLoading(true);
    setStatementError(null);
    api.get(`/finance/provider/${clinicId}/statement`)
      .then((res) => setStatement(res?.data || res))
      .catch((err) => {
        // P0-FIN-ISOLATION-01 — clear before surfacing the error.
        setStatement(null);
        setStatementError(err?.response?.data?.message || err?.message || t('provider.finance.overview.errorGeneric'));
      })
      .finally(() => setStatementLoading(false));
  }, [clinicId, t]);

  useEffect(() => { loadStatement(); }, [loadStatement]);

  // Reuse existing tenant-scoped Provider endpoint for customer search.
  useEffect(() => {
    setReceivablesLoading(true);
    api.get('/clinic/accountant/receivables')
      .then((res) => setReceivables((res?.data || res)?.items || []))
      .catch(() => setReceivables([]))
      .finally(() => setReceivablesLoading(false));
  }, []);

  const enrichedBuckets = useMemo(() => {
    const buckets = statement?.buckets || [];
    return buckets.map((b) => ({
      ...b,
      // Provider NET = frozen. Gross Revenue = Net + Fees & Taxes.
      // Fees & Taxes derived from the same canonical rollup (we do NOT
      // recompute commission/VAT — this is just Gross - Net using the
      // frozen milestone snapshot values on the backend statement).
      // Because the statement endpoint returns `earned_micros` = frozen
      // Provider NET across released milestones, we can back-solve fees
      // as long as gross ≥ net. We ask the backend for `earned` (NET);
      // "Gross Revenue" here is presented as a derived total for the
      // Provider from the same rail bucket: fees_and_taxes_micros_derived
      // is filled by the booking-detail rollup, so at the overview
      // level we show it as ((funded_micros - net) is not the same);
      // instead show Fees & Taxes as `—` at overview and only expose
      // exact Fees & Taxes on the booking drill-down where canonical
      // rows exist. Overview keeps: Customer Funds, Net Earnings,
      // Pending Settlement, Settled, Reversed.
      fees_and_taxes_micros_derived: 0,
    }));
  }, [statement]);

  const filteredReceivables = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return receivables;
    return receivables.filter((r) => {
      const hay = [
        r.patient_name,
        r.booking_id,
        r.order_id,
        r.treatment_name,
        r.created_at,
        r.status,
      ].filter(Boolean).map(String).map((s) => s.toLowerCase()).join(' ');
      return hay.includes(q);
    });
  }, [receivables, searchQuery]);

  return (
    <div className="min-h-full bg-slate-50" data-testid="provider-finance">
      {/* SECTION HEADER */}
      <div className="px-5 pt-4 pb-2 flex items-center gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            {t('provider.finance.section')}
          </div>
          <div className="text-[15px] font-semibold text-slate-800" data-testid="finance-title">
            {t('provider.finance.title')}
          </div>
        </div>
        <button
          type="button"
          onClick={loadStatement}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-200 hover:border-slate-300 text-slate-700"
          data-testid="finance-refresh-btn"
        >
          <RefreshCw className={`h-3 w-3 ${statementLoading ? 'animate-spin' : ''}`} />
          {t('provider.finance.refresh')}
        </button>
      </div>

      {/* OVERVIEW — Customer + Earnings position per (PSP, currency) rail */}
      <section className="px-5 pb-4">
        {statementLoading && (
          <div className="text-[12px] text-slate-500" data-testid="finance-overview-loading">
            {t('provider.finance.overview.loading')}
          </div>
        )}
        {statementError && !statementLoading && (
          <div className="text-[12px] text-rose-700" data-testid="finance-overview-error">
            {statementError}
          </div>
        )}
        {!statementLoading && !statementError && enrichedBuckets.length === 0 && (
          <div className="text-[12px] text-slate-500 border border-dashed border-slate-200 bg-white rounded p-6 text-center"
               data-testid="finance-overview-empty">
            {t('provider.finance.overview.empty')}
          </div>
        )}
        {!statementLoading && enrichedBuckets.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="finance-buckets-grid">
            {enrichedBuckets.map((b, i) => (
              <BucketCard key={`${b.psp_provider}-${b.currency}-${i}`} bucket={b} />
            ))}
          </div>
        )}
      </section>

      {/* CUSTOMER SEARCH */}
      <section className="px-5 pb-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          {t('provider.finance.search.title')}
        </div>
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('provider.finance.search.placeholder')}
            className="w-full pl-8 pr-2 py-1.5 border border-slate-200 rounded text-[13px] focus:outline-none focus:border-slate-400"
            data-testid="finance-search-input"
          />
        </div>

        <div className="overflow-x-auto border border-slate-200 rounded bg-white">
          <table className="w-full text-[12px]" data-testid="finance-customers-table">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-2 py-1.5">{t('provider.finance.search.col.patient')}</th>
                <th className="text-left px-2 py-1.5">{t('provider.finance.search.col.treatment')}</th>
                <th className="text-left px-2 py-1.5">{t('provider.finance.search.col.booking')}</th>
                <th className="text-left px-2 py-1.5">{t('provider.finance.search.col.date')}</th>
                <th className="text-right px-2 py-1.5">{t('provider.finance.search.col.amount')}</th>
                <th className="text-left px-2 py-1.5">{t('provider.finance.search.col.status')}</th>
                <th className="text-right px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {receivablesLoading && (
                <tr><td colSpan={7} className="px-2 py-4 text-slate-500 text-center">
                  {t('provider.finance.search.loading')}
                </td></tr>
              )}
              {!receivablesLoading && filteredReceivables.length === 0 && (
                <tr><td colSpan={7} className="px-2 py-4 text-slate-500 text-center" data-testid="finance-customers-empty">
                  {t('provider.finance.search.empty')}
                </td></tr>
              )}
              {!receivablesLoading && filteredReceivables.map((r) => (
                <tr key={r.order_id || r.booking_id} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 text-slate-800">{r.patient_name || r.patient_id || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-700">{r.treatment_name || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-700 font-mono text-[11px]">{r.booking_id || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-700">{fmtDate(r.created_at)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-800">
                    {r.amount != null ? new Intl.NumberFormat(undefined, {
                      style: 'currency', currency: r.currency || 'EUR', maximumFractionDigits: 2,
                    }).format(Number(r.amount) || 0) : '—'}
                  </td>
                  <td className="px-2 py-1.5">
                    <Pill tone={r.status === 'PAID' ? 'emerald' : r.status === 'REFUNDED' ? 'rose' : 'amber'}>
                      {r.status ? t(`common.status.${r.status}`, { defaultValue: r.status }) : '—'}
                    </Pill>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {r.booking_id && (
                      <button
                        type="button"
                        onClick={() => setSelectedBookingId(r.booking_id)}
                        className="text-[11px] text-slate-700 hover:text-slate-900 underline"
                        data-testid={`open-booking-${r.booking_id}`}
                      >
                        {t('provider.finance.search.open')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* TREATX MIXED SETTLEMENT — provider-declared direct collections.
          Mongo-sourced, so bookings collected entirely in cash (which have no
          escrow row) are visible here even though the PG drill-down cannot
          see them. */}
      <div className="px-4 pb-6">
        <DirectCollectionsPanel clinicId={clinicId} />
      </div>

      {selectedBookingId && (
        <BookingDetailDrawer
          clinicId={clinicId}
          bookingId={selectedBookingId}
          onClose={() => setSelectedBookingId(null)}
        />
      )}
    </div>
  );
};

export default ProviderFinance;
